import {ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit} from '@angular/core';
import {MeasurementsService} from '../measurements.service';
import {map, uniq} from 'lodash-es';
import {GeometryObject} from 'geojson';
import {MapElement} from '../../shared/map/map-element.model';
import {Subject} from 'rxjs';
import {SidebarPanelComponent} from '../../sidebar/sidebar-panel/sidebar-panel.component';
import {AppMessageService} from '../../shared/app-message/app-message.service';
import {StateService} from '../../shared/state/state.service';
import {TranslateService} from '@ngx-translate/core';
import {_} from '../../shared/utils';
import * as measureDefinitions from '../measureDefinitions';
import {AuthService} from '../../auth/auth.service';
import {LinkingCrudService} from '../../linking/linking.service';
import {LandingPageTile} from '../../shared/tiles/home-tile.model';
import {FilterChangeEvent, FilterDefinitionEntry} from '../../element-utils/element-filters/element-filters';
import {BaseComponent} from '../../components/general/base/base.component';

declare const Plotly: any;

/**
 * This component is used to display measurement data history for current feature.
 * NGX Charts is used to display charts: https://swimlane.gitbooks.io/ngx-charts/
 *
 * TODO: measurements not in use, needs full-stack re-implementation
 */
@Component({
  selector: 'qm-measurements-feature',
  templateUrl: './measurements-feature.component.html',
  styleUrls: ['./measurements-feature.component.scss']
})
export class MeasurementsFeatureComponent extends BaseComponent implements OnInit, OnDestroy {

  /**
   * Current map object
   */
  @Input()
  public currentFeature: MapElement<GeometryObject>;
  /**
   * List of measurements as Observable
   */
  public measurements: any[];
  /**
   * List of available measurements
   */
  public selectableMeasurements: any[];
  public filterDefinitions: FilterDefinitionEntry[];
  /**
   * Measurement data for chart, see ngx-charts documentation for more info
   */
  public chartData: any[];
  public initReady: boolean = false;
  public tozero = true;
  private layout = {
    title: 'Mittaushistoria',
    yaxis: {
      title: 'yaxis title',
      rangemode: this.getRangeMode()
    },
    yaxis2: {
      title: 'yaxis2 title',
      overlaying: 'y',
      rangemode: this.getRangeMode(),
      side: 'right'
    },
    yaxis3: {
      title: 'yaxis2 title',
      overlaying: 'y',
      rangemode: this.getRangeMode(),
      side: 'left'
    },
    yaxis4: {
      title: 'yaxis2 title',
      overlaying: 'y',
      rangemode: this.getRangeMode(),
      side: 'right'
    },
    yaxis5: {
      title: 'yaxis2 title',
      overlaying: 'y',
      rangemode: this.getRangeMode(),
      side: 'left'
    },
    legend: {
      orientation: 'v',
      x: 1,
      y: 100
    }
  };
  /**
   * Subscription manager
   * @type {Subject<any>}
   */
  private ngUnsubscribe: Subject<boolean> = new Subject();

  /**
   * Default constructor
   * @param {MeasurementsService} measurementsService
   * @param {AppMessageService} appMessageService
   * @param {ChangeDetectorRef} changeDetectorRef
   * @param {TranslateService} translate
   * @param {StateService} stateService
   * @param linkingService
   * @param {AuthService} auth
   */
  constructor(private measurementsService: MeasurementsService,
              protected appMessageService: AppMessageService,
              private changeDetectorRef: ChangeDetectorRef,
              private translate: TranslateService,
              private stateService: StateService,
              private readonly linkingCrudService: LinkingCrudService,
              private zone: NgZone,
              private auth: AuthService) {
    super();
    this.name = MeasurementsFeatureComponent.getComponentName();
  }

  private static getComponentName(): string {
    return 'MeasurementsFeature';
  }

  /**
   * Angular lifecycle hook, fetch measurement data
   */
  ngOnInit() {
    const tenant = 'T' + this.auth.getTenant();
    this.selectFeature(tenant);
  }

  /**
   * Angular destructor function
   */
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private async selectFeature(tenant: string) {
    this.initReady = false;
    console.log(this.currentFeature);
    if (measureDefinitions[tenant]) {
      const id = this.currentFeature.properties[this.currentFeature.layer.metadata.uniqueBy];
      const link = await this.linkingCrudService.filter({regoId: id});
      console.log(link);
      if (link.isOk && link.data.length === 1) {
        const m = link.data[0];
        const measurements = measureDefinitions[tenant]();
        console.log(measurements);
        this.selectableMeasurements = measureDefinitions[tenant]()[m.regoId];
        this.filterDefinitions = [{name: 'Select measurements', type: 'multi', filterValues: this.selectableMeasurements}];
        console.log(this.selectableMeasurements);
      }
      this.getDataForFeature(this.selectableMeasurements);
      this.changeDetectorRef.markForCheck();
    }
    this.initReady = true;
  }

  public filtersChanged($event: FilterChangeEvent) {
    /*
    this.startDate = $event.startDate;
    this.endDate = $event.endDate;
    this.selectedFilters = $event.selectedFilters;
    this.loadMore();
    */
    console.log($event);
    if ($event.filterDefinitions) {
      this.getDataForFeature($event.filterDefinitions.find(o => {
        return o.name === 'Select measurements';
      }).filterValues);
    }
  }

  public dateSelected(event) {
    console.log(event);
    if (this.selectableMeasurements) {
      this.getDataForFeature(this.selectableMeasurements);
    }
  }

  /**
   * Fetch measurement data for current feature and parse data to suit ngx-charts.
   */
  private async getDataForFeature(selectedMeasurements) {
    console.log('getDataForFeature', selectedMeasurements);
    if (!selectedMeasurements) {
      return;
    }
    this.chartData = [];
    this.changeDetectorRef.markForCheck();
    const pArr: Promise<any>[] = [];
    selectedMeasurements.filter(o => {
      return o.active;
    }).forEach(active => {
      pArr.push(new Promise<any>(async resolve => {
        const data = (await this.measurementsService.getMeasurementsForName(active.name)).reverse();
        resolve({
          axisTitle: active.quality,
          name: active.description,
          mode: 'line',
          x: map(data, row => {
            return row.createdAt;
          }),
          y: map(data, row => {
            return row.average;
          })
        });
      }));
    });
    const values = await Promise.all(pArr);
    console.log(values);
    // this.chartData = this.generate();
    this.chartData = values;
    console.log(this.chartData);
    const titles = uniq(this.chartData.map(o => {
      return o.axisTitle;
    }));
    console.log(titles);
    const valueArr = [];
    for (const data of this.chartData) {
      const title = data.axisTitle;
      const i = titles.findIndex(o => {
        return o === title;
      });
      if (i === 0) {
        this.layout.yaxis.title = title;
        this.layout.yaxis.rangemode = this.getRangeMode();
        valueArr.push({
          name: data.name,
          mode: data.mode,
          x: data.x,
          y: data.y,
        });
      } else {
        this.layout['yaxis' + (i + 1)].title = title;
        this.layout['yaxis' + (i + 1)].rangemode = this.getRangeMode();
        valueArr.push({
          name: data.name,
          mode: data.mode,
          yaxis: 'y' + (i + 1),
          x: data.x,
          y: data.y
        });
      }
    }
    console.log(this.layout);
    this.loading = false;
    this.changeDetectorRef.markForCheck();
    const sub = this.zone.onStable.subscribe(() => {
      if (this.chartData.length > 0 && Plotly) {
        Plotly.newPlot('plotly', valueArr, this.layout, {
          modeBarButtonsToRemove: ['sendDataToCloud'],
          displaylogo: false
        });
      }
      sub.unsubscribe();
    });
  }

  private getRangeMode(): string {
    return this.tozero ? 'tozero' : 'normal';
  }
}
